import type {
  ConversationDebugMessageDTO,
  ConversationMessageMetadata,
  SearchFallbackDigest,
} from "@shaarp/common";
import type { UIMessage } from "ai";
import { formatDate } from "./format";

/**
 * A turn that did not land the way it should have.
 *
 * `scope` matters as much as the incident: `persisted` came out of the database
 * and will still be there next week, `session` exists only in this tab. Chat
 * errors are never stored, so they are all `session` and disappear on reload —
 * showing them side by side without saying so would suggest the panel forgets
 * things.
 */
export type Incident = {
  key: string;
  messageId: string;
  at: string;
  scope: "persisted" | "session";
  /** Translation key: this module is pure and cannot resolve it itself. */
  labelKey: string;
  /** The counts the label is translated with, when it has any. */
  labelValues?: Record<string, number>;
  detail: string | null;
  cause: string | null;
  retryable: boolean | null;
  /** How the offending message opens and closes, to find it in the chat. */
  contentStart: string | null;
  contentEnd: string | null;
};

/** The message types that count as an incident, in the order they are reported. */
const INCIDENT_TYPES = ["INTERRUPTED", "TRUNCATED"] as const;

/** Words quoted on each side of a message, matching what the API returns. */
const PREVIEW_WORDS = 6;

/**
 * The opening and closing words of a UI message.
 *
 * Only needed for the session-only incidents: everything the API returns already
 * carries its own bounds, computed the same way. No marker stripping here, unlike
 * the API side — the message quoted is the user's question, and `[@shaarp/...]`
 * markers only ever appear in what the assistant writes.
 */
const toContentBounds = (
  message: UIMessage<ConversationMessageMetadata>
): { start: string | null; end: string | null } => {
  const text = message.parts
    .filter((part): part is { type: "text"; text: string } =>
      Boolean(part.type === "text" && "text" in part)
    )
    .map((part) => part.text)
    .join(" ");
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { start: null, end: null };

  return {
    start: words.slice(0, PREVIEW_WORDS).join(" "),
    end:
      words.length > PREVIEW_WORDS * 2
        ? words.slice(-PREVIEW_WORDS).join(" ")
        : null,
  };
};

const toPersistedIncident = (
  message: ConversationDebugMessageDTO,
  labelKey: string
): Incident => ({
  key: `persisted-${message.id}`,
  messageId: message.id,
  at: formatDate(message.at),
  scope: "persisted",
  labelKey,
  detail: null,
  cause: null,
  retryable: null,
  contentStart: message.contentStart,
  contentEnd: message.contentEnd,
});

/** The research tools whose calls carry a {@link SearchFallbackDigest}. */
const FALLBACK_TOOL_TYPES = ["tool-webSearch", "tool-readPages"];

/**
 * A turn whose research a provider answered in place of the other: Exa when
 * Tavily failed or found nothing, Tavily when Exa did on the routes it leads,
 * and Exa reading the pages Tavily could not — page reading is Tavily first on
 * every route.
 *
 * The answer reads the same either way, which is why they are incidents: an
 * outage of the primary search provider is otherwise invisible from the chat.
 */
const toFallbackIncidents = (
  message: ConversationDebugMessageDTO
): Incident[] => {
  let queries = 0;
  let pages = 0;
  let tavilyQueries = 0;
  for (const call of message.toolCalls) {
    if (!FALLBACK_TOOL_TYPES.includes(call.type)) continue;
    const digest = (call.output as { searchFallback?: SearchFallbackDigest })
      ?.searchFallback;
    queries += digest?.exaQueries ?? 0;
    pages += digest?.exaPages ?? 0;
    tavilyQueries += digest?.tavilyQueries ?? 0;
  }

  const incidents: Incident[] = [];
  if (queries > 0) {
    incidents.push({
      ...toPersistedIncident(
        message,
        "chat.debug.incidents.kinds.searchFallback"
      ),
      key: `fallback-${message.id}`,
      labelValues: { queries },
    });
  }
  // Apart from the searches: reading a page Tavily could not fetch says
  // nothing about who answered the searches of the turn.
  if (pages > 0) {
    incidents.push({
      ...toPersistedIncident(
        message,
        "chat.debug.incidents.kinds.pageReadFallback"
      ),
      key: `fallback-pages-${message.id}`,
      labelValues: { pages },
    });
  }
  if (tavilyQueries > 0) {
    incidents.push({
      ...toPersistedIncident(
        message,
        "chat.debug.incidents.kinds.searchFallbackTavily"
      ),
      key: `fallback-tavily-${message.id}`,
      labelValues: { queries: tavilyQueries },
    });
  }
  return incidents;
};

/**
 * The message that identifies a chat error is the question that preceded it: the
 * message the error is attached to carries the error text, not what was asked.
 */
const toSessionIncident = (
  message: UIMessage<ConversationMessageMetadata>,
  askedBefore: UIMessage<ConversationMessageMetadata> | undefined
): Incident | null => {
  const messageError = message.metadata?.error;
  if (!messageError) return null;

  const bounds = toContentBounds(askedBefore ?? message);

  return {
    key: `session-${message.id}`,
    messageId: message.id,
    at: message.metadata?.createdAt
      ? formatDate(message.metadata.createdAt)
      : "—",
    scope: "session",
    labelKey: "chat.debug.incidents.kinds.chatError",
    detail: messageError.message,
    cause: messageError.cause ?? null,
    retryable: messageError.retryable,
    contentStart: bounds.start,
    contentEnd: bounds.end,
  };
};

/**
 * Every incident of the conversation, durable ones first.
 *
 * @param debugMessages What the API returned, or none while it is loading.
 * @param uiMessages The messages the browser is holding.
 */
export const collectIncidents = (
  debugMessages: ConversationDebugMessageDTO[] | undefined,
  uiMessages: UIMessage<ConversationMessageMetadata>[]
): Incident[] => {
  const persisted = (debugMessages ?? []).flatMap((message) => {
    return [
      ...(INCIDENT_TYPES.some((type) => type === message.type)
        ? [
            toPersistedIncident(
              message,
              `chat.debug.incidents.kinds.${message.type}`
            ),
          ]
        : []),
      ...toFallbackIncidents(message),
    ];
  });

  const session = uiMessages.flatMap((message, index) => {
    const incident = toSessionIncident(message, uiMessages[index - 1]);
    return incident ? [incident] : [];
  });

  return [...persisted, ...session];
};
